import { useState } from 'react';

import { GAP_TYPE_META } from '../gap-meta';
import { deleteGap } from '../state/gaps';
import { useAppConfig } from '../state/config';
import type { Gap } from '../types';

import { GapComposer } from './GapComposer';
import { GapQuickViewDialog } from './GapQuickViewDialog';
import { GapTypeBadge } from './GapTypeBadge';
import { Modal } from './Modal';
import { NewFormRequiredDialog } from './NewFormRequiredDialog';

/**
 * Compact card surface for a single gap. The card itself is clickable and
 * opens a read-only quick-view modal; Edit and Delete are reached from that
 * modal and route through the shared create dialogs (GapComposer /
 * NewFormRequiredDialog in edit mode) instead of an inline form.
 */
export function GapCard({
  gap,
  onChanged,
}: {
  gap: Gap;
  onChanged?: () => void;
}) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function runDelete() {
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteGap(gap.id);
      setConfirmOpen(false);
      setQuickOpen(false);
      onChanged?.();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  }

  const isNewForm = gap.gap_type === 'new_form_required';

  return (
    <>
      <button
        type="button"
        onClick={() => setQuickOpen(true)}
        className="card p-3 flex flex-col gap-2 max-h-[13rem] text-left w-full hover:border-slate-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 transition overflow-hidden"
      >
        <div className="flex items-start justify-between gap-2">
          <GapTypeBadge type={gap.gap_type} />
          <GeoChip geoId={gap.geography} />
        </div>

        {isNewForm && gap.proposed_form_name && (
          <div
            className="text-sm font-semibold text-slate-900 truncate"
            title={gap.proposed_form_name}
          >
            {gap.proposed_form_name}
          </div>
        )}

        {gap.scope === 'form' && gap.form_name_snapshot && (
          <div
            className="text-xs text-slate-500 truncate"
            title={gap.form_name_snapshot}
          >
            {gap.form_name_snapshot}
          </div>
        )}

        {gap.field_name && (
          <div
            className="text-[11px] inline-flex items-center gap-1 self-start bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5"
            title={`Targets variable ${gap.field_name}`}
          >
            field <span className="font-mono">{gap.field_name}</span>
          </div>
        )}

        <p
          className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-3 overflow-hidden"
        >
          {gap.description}
        </p>

        <div className="mt-auto flex items-baseline justify-between gap-3 pt-1 text-xs text-slate-500">
          <span className="truncate min-w-0" title={gap.author_name}>
            {gap.author_name}
          </span>
          <span className="text-slate-400 shrink-0 whitespace-nowrap">
            {formatShortDate(gap.updated_at)}
          </span>
        </div>
      </button>

      {quickOpen && (
        <GapQuickViewDialog
          gap={gap}
          onClose={() => setQuickOpen(false)}
          onEdit={() => {
            setQuickOpen(false);
            setEditOpen(true);
          }}
          onDelete={() => {
            setDeleteError(null);
            setConfirmOpen(true);
          }}
        />
      )}

      {editOpen && !isNewForm && (
        <GapComposer
          existing={gap}
          onCancel={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged?.();
          }}
        />
      )}

      {editOpen && isNewForm && (
        <NewFormRequiredDialog
          existing={gap}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged?.();
          }}
        />
      )}

      {confirmOpen && (
        <ConfirmDeleteGap
          gap={gap}
          busy={deleting}
          error={deleteError}
          onConfirm={runDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}

function ConfirmDeleteGap({
  gap,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  gap: Gap;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const headline =
    gap.gap_type === 'new_form_required' && gap.proposed_form_name
      ? gap.proposed_form_name
      : gap.form_name_snapshot || GAP_TYPE_META[gap.gap_type].label;
  return (
    <Modal
      title="Delete this gap?"
      size="md"
      onClose={busy ? undefined : onCancel}
    >
      <p className="text-sm text-slate-600">
        <span className="font-medium text-slate-800">{headline}</span>
        <br />
        This action cannot be undone.
      </p>
      {error && (
        <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      <div className="mt-5 flex items-center justify-end gap-2">
        <button className="btn-ghost h-9" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-rose-600 text-white text-sm font-medium px-3.5 py-2 shadow-sm hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors h-9"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Deleting…' : 'Delete gap'}
        </button>
      </div>
    </Modal>
  );
}

function GeoChip({ geoId }: { geoId: string }) {
  const cfg = useAppConfig();
  const g = cfg.geographies.find((x) => x.id === geoId);
  if (!g) return <span className="chip font-mono">{geoId}</span>;
  return (
    <span
      className="chip bg-slate-100 text-slate-700 font-mono font-semibold flex-shrink-0"
      title={g.name}
    >
      {g.code}
    </span>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
